import { useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import type { FreeformTextBlock, FreeformTextTitleLevel } from '@course-authoring/shared-types';
import { isRichTextContentEmpty, richTextToPlainText, useTheme } from '@course-authoring/shared-types';

import { InteractionRichTextField } from './InteractionRichTextField';
import { createTokenRichTextToolbarStyles, EMPTY_RICH_TEXT_DOC } from './interactionEditorUtils';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';
import { QuestionSelector } from './questionBank/QuestionSelector';
import { resolveQuestionEditorMode } from './questionBank/questionEditorShared';
import { FieldOverrideStatus } from './questionBank/FieldOverrideStatus';
import { useFieldOverrideState } from './questionBank/useFieldOverrideState';
import { useQuestionTextOverride } from './questionBank/useQuestionTextOverride';
import type { FreeformTextQuestionDraftBlock } from './questionBank/questionBlockConversion';

export type FreeformTextPurpose = 'Sentiment' | 'Reflection';

const PURPOSE_OPTIONS: Array<{ value: FreeformTextPurpose; label: string }> = [
  { value: 'Sentiment', label: 'Sentiment' },
  { value: 'Reflection', label: 'Reflection' },
];

export interface FreeformTextEditorProps {
  /**
   * Block mode receives the real persisted `FreeformTextBlock` (always
   * structurally valid here, since `purpose` is optional). Modal mode
   * (Question Bank create/edit) additionally carries a transient `purpose`
   * field that has no lesson-block counterpart — see
   * `FreeformTextQuestionDraftBlock`.
   */
  block: FreeformTextQuestionDraftBlock;
  onChange: (updates: Record<string, unknown>) => void;
  autoFocus?: boolean;
  /**
   * 'block' (default): lesson-block mode — title controls + QuestionSelector,
   * question content overridable from the resolved block fields, no Purpose
   * control (bank-only metadata).
   * 'questionModal': create/edit-question modal mode — editable question
   * text, no title controls, Purpose control shown.
   */
  variant?: 'block' | 'questionModal';
  translationMode?: boolean;
}

type FieldName = 'title' | 'description';

export function FreeformTextEditor({
  block,
  onChange,
  autoFocus = false,
  variant = 'block',
  translationMode = false,
}: FreeformTextEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedField, setFocusedField] = useState<FieldName | null>(null);

  const { isModal, isManaged, showBlockAuthoringControls } = resolveQuestionEditorMode(
    variant,
    block,
    translationMode,
  );

  const titleLevel = block.titleLevel ?? 'h2';
  const descriptionText = richTextToPlainText(block.description);

  const handleFieldFocus = useCallback((field: FieldName) => setFocusedField(field), []);
  const handleFieldBlur = useCallback(() => setFocusedField(null), []);

  // NA-382: question text is authored on the block (or on the bank value in the
  // modal); see useQuestionTextOverride for why the commit is on blur.
  const questionText = useQuestionTextOverride(block, onChange, isModal, translationMode);

  // NA-384: title, same override model, plain string instead of rich text.
  const titleOverride = useFieldOverrideState<FreeformTextBlock, string>(block, {
    value: 'title',
    marker: 'titleOverridden',
    bankValue: 'titleBankValue',
    requireBankValue: true,
  });
  const hasBankTitle = block.titleBankValue !== undefined;

  const handleTitleChange = useCallback(
    (next: string) => onChange(
      hasBankTitle && !translationMode ? titleOverride.buildEdit(next) : { title: next },
    ),
    [hasBankTitle, translationMode, titleOverride, onChange],
  );

  const handleTitleRevert = useCallback(
    () => onChange(titleOverride.buildRevert()),
    [titleOverride, onChange],
  );

  const richTextToolbarStyles = useMemo(() => createTokenRichTextToolbarStyles(tokens), [tokens]);

  // Purpose is bank-level-only metadata (NA-389/NA-444): no block-level
  // override concept, no revert action, no "Edited" indicator — editable
  // only in the modal, carried on the editing-scratch object only.
  const purpose: FreeformTextPurpose = block.purpose === 'Reflection' ? 'Reflection' : 'Sentiment';

  const isQuestionTextEmpty = isRichTextContentEmpty(questionText.content);

  const styles = useMemo(
    (): Record<string, CSSProperties> => ({
      container: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacing.md,
        maxWidth: '100%',
        boxSizing: 'border-box',
      },
      header: {
        marginBottom: tokens.spacing.xs,
      },
      titleInput: {
        ...getTitleInputStyle(titleLevel, tokens),
        marginBottom: tokens.spacing.sm,
      },
      questionTextLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: tokens.text.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: tokens.spacing.xs,
      },
      descriptionEditor: {
        fontFamily: tokens.typography.fontFamily,
        fontSize: tokens.typography.body.size,
        color: tokens.text.muted,
        lineHeight: tokens.typography.body.lineHeight,
      },
      warningBanner: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacing.sm,
        padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
        borderRadius: tokens.radius.md,
        border: `1px solid ${tokens.ui.workflowDraft}`,
        backgroundColor: `color-mix(in srgb, ${tokens.ui.workflowDraft} 12%, transparent)`,
        fontFamily: tokens.typography.fontFamily,
        fontSize: '13px',
        color: tokens.text.primary,
        lineHeight: 1.4,
      },
      warningIcon: {
        fontSize: '14px',
        color: tokens.ui.workflowDraft,
        marginTop: '2px',
        flexShrink: 0,
      },
      purposeSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacing.xs,
        marginTop: tokens.spacing.sm,
      },
      purposeLabel: {
        fontSize: '12px',
        fontWeight: 600,
        color: tokens.text.muted,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      },
      purposeGroup: {
        display: 'flex',
        gap: tokens.spacing.sm,
      },
      purposeButton: {
        minWidth: '110px',
        padding: `${tokens.spacing.xs} ${tokens.spacing.md}`,
        borderRadius: tokens.radius.sm,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: tokens.borders.default,
        backgroundColor: tokens.backgrounds.app,
        color: tokens.text.primary,
        fontFamily: tokens.typography.fontFamily,
        fontSize: tokens.typography.body.size,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background-color 150ms, color 150ms, border-color 150ms',
      },
      purposeButtonActive: {
        backgroundColor: tokens.brand.primary,
        color: tokens.text.onBrand,
        borderColor: tokens.brand.primary,
      },
    }),
    [titleLevel, tokens],
  );

  return (
    <div ref={containerRef} style={styles.container}>
      {!isModal && (
        <div style={styles.header}>
          {showBlockAuthoringControls && (
            <TitleLevelSelector
              currentLevel={titleLevel}
              onSelectLevel={(level) => onChange({ titleLevel: level as FreeformTextTitleLevel })}
              tokens={tokens}
            />
          )}

          <input
            type="text"
            value={titleOverride.displayValue ?? ''}
            placeholder="Enter title..."
            onChange={(event) => handleTitleChange(event.target.value)}
            onFocus={() => handleFieldFocus('title')}
            onBlur={handleFieldBlur}
            style={styles.titleInput}
            autoFocus={autoFocus}
          />
          {!translationMode && (
            <FieldOverrideStatus
              isDiverged={titleOverride.isDiverged}
              onRevert={handleTitleRevert}
              fieldLabel="title"
            />
          )}

          {showBlockAuthoringControls && (
            <QuestionSelector
              block={block}
              onChange={onChange}
            />
          )}
        </div>
      )}

      {(isModal || isManaged) && (
        <>
          {questionText.isEditable ? (
            <div>
              {isModal ? (
                <div style={styles.questionTextLabel}>Question text</div>
              ) : (
                <div style={styles.questionTextLabel}>
                  Question text
                  {questionText.showStatus && (
                    <FieldOverrideStatus
                      isDiverged={questionText.override.isDiverged}
                      onRevert={questionText.onRevert}
                      fieldLabel="question text"
                    />
                  )}
                </div>
              )}
              <InteractionRichTextField
                content={questionText.content ?? EMPTY_RICH_TEXT_DOC}
                placeholder="Enter question text..."
                isFocused={focusedField === 'description'}
                onFocus={() => handleFieldFocus('description')}
                onBlur={handleFieldBlur}
                onContentChange={questionText.onContentChange}
                autoFocus={autoFocus}
                containerRef={containerRef}
                editorClassName="tiptap freeform-text-editor"
                editorStyle={styles.descriptionEditor}
                toolbarPreset="full"
                toolbarStyles={richTextToolbarStyles}
              />
            </div>
          ) : descriptionText !== '' ? (
            // Translation mode, question text NOT overridden: stays Question
            // Bank-owned, shown as read-only localized context.
            <div>
              <div style={styles.questionTextLabel}>Question text</div>
              <div style={styles.descriptionEditor}>{descriptionText}</div>
            </div>
          ) : null}

          {isModal && (
            <div style={styles.purposeSection}>
              <span style={styles.purposeLabel}>Purpose</span>
              <div style={styles.purposeGroup} role="group" aria-label="Purpose">
                {PURPOSE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onChange({ purpose: option.value })}
                    style={{
                      ...styles.purposeButton,
                      ...(purpose === option.value ? styles.purposeButtonActive : {}),
                    }}
                    aria-pressed={purpose === option.value}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isModal && isQuestionTextEmpty && (
            <div style={styles.warningBanner} role="alert">
              <span style={styles.warningIcon} aria-hidden="true">
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: 14 }} />
              </span>
              <span>Enter question text or learners will have nothing to respond to.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

FreeformTextEditor.displayName = 'FreeformTextEditor';
